import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Eye, Package, Truck, CheckCircle, XCircle, Trash2, Clock, CreditCard, Printer, Mail, X } from 'lucide-react';
import axios from 'axios';
import { formatPrice } from '../../utils/priceFormatter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  pending_payment: { label: 'Paiement en attente', color: 'bg-orange-100 text-orange-800', icon: CreditCard },
  confirmed: { label: 'Confirmée', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  processing: { label: 'En traitement', color: 'bg-blue-100 text-blue-800', icon: Package },
  shipped: { label: 'Expédiée', color: 'bg-purple-100 text-purple-800', icon: Truck },
  delivered: { label: 'Livrée', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const paymentStatusConfig = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  paid: { label: 'Payé', color: 'bg-green-100 text-green-800' },
  failed: { label: 'Échoué', color: 'bg-red-100 text-red-800' },
  refunded: { label: 'Remboursé', color: 'bg-gray-100 text-gray-800' }
};

// Helper function to get customer full name
const getCustomerName = (customer) => {
  if (!customer) return 'N/A';
  const firstName = customer.firstName || customer.first_name || '';
  const lastName = customer.lastName || customer.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || customer.name || 'N/A';
};

// Helper function to get customer email
const getCustomerEmail = (customer) => {
  return customer?.email || 'N/A';
};

// Helper function to get customer phone
const getCustomerPhone = (customer) => {
  return customer?.phone || 'N/A';
};

const AdminOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('contact@toilestorebanne.com');
  const [sendingEmail, setSendingEmail] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchOrders();
  }, [navigate]);

  useEffect(() => {
    const filtered = orders.filter(order => {
      const searchLower = searchQuery.toLowerCase();
      const orderNumber = (order.order_number || '').toLowerCase();
      const customerName = getCustomerName(order.customer).toLowerCase();
      const customerEmail = getCustomerEmail(order.customer).toLowerCase();
      
      return orderNumber.includes(searchLower) ||
        customerName.includes(searchLower) ||
        customerEmail.includes(searchLower);
    });
    setFilteredOrders(filtered);
  }, [searchQuery, orders]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/orders/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
      setFilteredOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleDeleteClick = (order, e) => {
    e.stopPropagation();
    setOrderToDelete(order);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/orders/${orderToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowDeleteConfirm(false);
      setOrderToDelete(null);
      if (selectedOrder?.id === orderToDelete.id) {
        setShowModal(false);
      }
      await fetchOrders();
      alert('Commande supprimée avec succès');
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Erreur lors de la suppression de la commande');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  // Fonction d'impression
  const handlePrint = () => {
    const printContent = generatePrintContent(selectedOrder);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Fonction d'envoi par email
  const handleSendEmail = async () => {
    if (!emailAddress || !emailAddress.includes('@')) {
      alert('Veuillez entrer une adresse email valide');
      return;
    }
    
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/orders/${selectedOrder.id}/send-email`,
        { email: emailAddress },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Commande envoyée à ${emailAddress}`);
      setShowEmailModal(false);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Erreur lors de l\'envoi de l\'email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Générer le contenu HTML pour l'impression
  const generatePrintContent = (order) => {
    if (!order) return '';
    
    const customer = order.customer || {};
    const addr = order.deliveryAddress || {};
    const customerName = `${customer.civility || ''} ${customer.firstName || ''} ${customer.lastName || ''}`.trim();
    
    const fixationLabels = {
      'petit-ourlet': 'Petit ourlet', 'grand-ourlet': 'Grand ourlet', 'jonc': 'Jonc',
      'jonc-magnetique': 'Jonc magnétique', 'fourreau': 'Fourreau', 'scratch': 'Scratch / Velcro', 'sangle': 'Sangle'
    };
    const lambrequinLabels = { 'droit': 'Coupe droite', 'ondule': 'Vague 1', 'vagues': 'Vague 2', 'sans': 'Sans' };
    const assemblageLabels = { 'haute-tenacite': 'Haute ténacité', 'tenara': 'Tenara', 'standard': 'Standard' };
    
    let itemsHtml = '';
    order.items?.forEach((item, index) => {
      const config = item.configuration || {};
      const optionsSupp = config.optionsSupp || {};
      const fabricCalc = config.fabricCalculation || {};
      
      let optionsList = [];
      if (optionsSupp.renfortLyre) optionsList.push('Renfort de lyre');
      if (optionsSupp.textePersonnalise) optionsList.push('Jonc magnétique');
      if (optionsSupp.agrafes) optionsList.push('Jonc montage rapide');
      if (optionsSupp.clips) optionsList.push('Assemblage spécial');
      if (optionsSupp.oeillets) optionsList.push('Lestage lambrequin');
      
      itemsHtml += `
        <div style="border: 1px solid #ddd; margin-bottom: 15px; page-break-inside: avoid;">
          <div style="background: #f5f5f5; padding: 10px; border-bottom: 1px solid #ddd;">
            <strong style="font-size: 14px;">${item.productName || item.product_name || 'Produit'}</strong>
            <span style="float: right;">${(item.totalPrice || item.total_price || 0).toFixed(2)} €</span>
          </div>
          <div style="padding: 10px;">
            <table style="width: 100%; font-size: 12px;">
              <tr>
                <td style="width: 50%; vertical-align: top; padding-right: 10px;">
                  <p><strong>Dimensions:</strong> ${config.largeur || config.width} × ${config.projection || config.avancee} cm</p>
                  <p><strong>Fixation haute:</strong> ${fixationLabels[config.fixationHaute] || config.fixationHaute || 'N/A'}${config.diametreJoncHaute ? ` <span style="color: #2196F3;">(Ø ${config.diametreJoncHaute} mm)</span>` : ''}</p>
                  <p><strong>Fixation basse:</strong> ${fixationLabels[config.fixationBasse] || config.fixationBasse || 'N/A'}${config.diametreJoncBasse ? ` <span style="color: #2196F3;">(Ø ${config.diametreJoncBasse} mm)</span>` : ''}</p>
                  <p><strong>Assemblage:</strong> ${assemblageLabels[config.typeAssemblage] || config.typeAssemblage || 'N/A'}</p>
                </td>
                <td style="width: 50%; vertical-align: top;">
                  <p><strong>Lambrequin:</strong> ${config.lambrequin && config.lambrequin !== 'sans' ? `${lambrequinLabels[config.lambrequin] || config.lambrequin} - ${config.hauteurLambrequin || config.hauteur_lambrequin} cm` : 'Sans'}</p>
                  ${config.fixationLambrequin ? `<p><strong>Fixation lamb.:</strong> ${fixationLabels[config.fixationLambrequin] || config.fixationLambrequin}${(config.diametreJoncLambrequin || config.diametreLambrequin) ? ` <span style="color: #2196F3;">(Ø ${config.diametreJoncLambrequin || config.diametreLambrequin} mm)</span>` : ''}</p>` : ''}
                  ${optionsList.length > 0 ? `<p><strong>Options:</strong> ${optionsList.join(', ')}</p>` : ''}
                </td>
              </tr>
            </table>
            ${fabricCalc.metrage_total ? `
              <div style="margin-top: 10px; padding: 8px; background: #e8f5e9; border-radius: 4px;">
                <strong style="color: #2e7d32;">Fabrication:</strong>
                Largeur totale: ${fabricCalc.largeur_totale} cm | 
                Avancée totale: ${fabricCalc.avancee_totale} cm | 
                Laizes: ${fabricCalc.nombre_laizes} | 
                Métrage: ${fabricCalc.metrage_total} m | 
                Poids: ${(fabricCalc.poids_total / 1000).toFixed(2)} kg
              </div>
            ` : ''}
          </div>
        </div>
      `;
    });
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Commande ${order.order_number}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #2ECC71; padding-bottom: 20px; }
          .header h1 { margin: 0; color: #2ECC71; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
          .info-box { border: 1px solid #ddd; padding: 15px; border-radius: 5px; }
          .info-box h3 { margin: 0 0 10px 0; color: #2ECC71; font-size: 14px; border-bottom: 1px solid #eee; padding-bottom: 5px; }
          .info-box p { margin: 5px 0; font-size: 12px; }
          .total-box { text-align: right; margin-top: 20px; padding: 15px; background: #f9f9f9; border-radius: 5px; }
          .total-box .total { font-size: 18px; font-weight: bold; color: #2ECC71; }
          @media print { body { margin: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🇫🇷 Toile Store Banne™</h1>
          <p>Commande <strong>${order.order_number}</strong></p>
          <p>Date: ${formatDate(order.createdAt)}</p>
        </div>
        
        <div class="info-grid">
          <div class="info-box">
            <h3>Client</h3>
            <p><strong>${customerName || 'N/A'}</strong></p>
            <p>Email: ${customer.email || 'N/A'}</p>
            <p>Tél: ${customer.phone || 'N/A'}</p>
            ${customer.isProfessional ? `<p>Société: ${customer.companyName || 'N/A'}</p>` : ''}
          </div>
          <div class="info-box">
            <h3>Livraison</h3>
            <p>${addr.address || addr.street || 'N/A'}</p>
            <p>${addr.postalCode || addr.postal_code || ''} ${addr.city || ''}</p>
            <p>${addr.country || 'France'}</p>
            ${order.deliveryMethod ? `<p><strong>Mode:</strong> ${order.deliveryMethod.name}</p>` : ''}
          </div>
        </div>
        
        <h3 style="color: #2ECC71; border-bottom: 1px solid #2ECC71; padding-bottom: 5px;">Articles commandés</h3>
        ${itemsHtml}
        
        <div class="total-box">
          <p>Sous-total: ${(order.subtotal || 0).toFixed(2)} €</p>
          <p>Livraison: ${(order.deliveryCost || 0).toFixed(2)} €</p>
          <p class="total">Total TTC: ${(order.totalTTC || 0).toFixed(2)} €</p>
        </div>
        
        <script>window.onload = function() { /* ready for print */ }</script>
      </body>
      </html>
    `;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get delivery address fields
  const getAddress = (deliveryAddress) => {
    if (!deliveryAddress) return {};
    return {
      street: deliveryAddress.address || deliveryAddress.street || '',
      city: deliveryAddress.city || '',
      postalCode: deliveryAddress.postalCode || deliveryAddress.postal_code || '',
      country: deliveryAddress.country || 'France'
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestion des commandes</h1>
                <p className="text-sm text-gray-600">{filteredOrders.length} commandes</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher par numéro, nom ou email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-celadon"
            />
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numéro</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paiement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.map((order) => {
                const StatusIcon = statusConfig[order.status]?.icon || Package;
                return (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{order.order_number || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{getCustomerName(order.customer)}</div>
                      <div className="text-sm text-gray-500">{getCustomerEmail(order.customer)}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-900">{formatDate(order.createdAt)}</td>
                    <td className="px-6 py-4 font-medium text-gray-900">{formatPrice(order.totalTTC || 0)} €</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${paymentStatusConfig[order.payment_status]?.color || 'bg-gray-100 text-gray-800'}`}>
                        {paymentStatusConfig[order.payment_status]?.label || order.payment_status || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full w-fit ${statusConfig[order.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                        <StatusIcon size={14} />
                        {statusConfig[order.status]?.label || order.status || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewOrder(order)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Voir les détails"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={(e) => handleDeleteClick(order, e)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredOrders.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Aucune commande trouvée
            </div>
          )}
        </div>
      </div>

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold">Détails de la commande</h2>
                <p className="text-gray-600 mt-1">{selectedOrder.order_number}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                  title="Imprimer"
                >
                  <Printer size={16} />
                  Imprimer
                </button>
                <button
                  onClick={() => setShowEmailModal(true)}
                  className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                  title="Envoyer par email"
                >
                  <Mail size={16} />
                  Email
                </button>
                <span className={`px-3 py-1 text-sm rounded-full ${paymentStatusConfig[selectedOrder.payment_status]?.color || 'bg-gray-100'}`}>
                  {paymentStatusConfig[selectedOrder.payment_status]?.label || selectedOrder.payment_status}
                </span>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order Status */}
              <div>
                <label className="block text-sm font-medium mb-2">Statut de la commande</label>
                <select
                  value={selectedOrder.status}
                  onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:border-celadon"
                >
                  <option value="pending">En attente</option>
                  <option value="pending_payment">Paiement en attente</option>
                  <option value="confirmed">Confirmée</option>
                  <option value="processing">En traitement</option>
                  <option value="shipped">Expédiée</option>
                  <option value="delivered">Livrée</option>
                  <option value="cancelled">Annulée</option>
                </select>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-medium mb-2">Informations client</h3>
                  <div className="text-sm space-y-2 bg-gray-50 p-4 rounded">
                    <div>
                      <label className="text-xs text-gray-500">Civilité:</label>
                      <p className="font-medium">{selectedOrder.customer?.civility || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Nom:</label>
                      <p className="font-medium">{getCustomerName(selectedOrder.customer)}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Email:</label>
                      <p className="font-medium">{getCustomerEmail(selectedOrder.customer)}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Téléphone:</label>
                      <p className="font-medium">{getCustomerPhone(selectedOrder.customer)}</p>
                    </div>
                    {selectedOrder.customer?.isProfessional && (
                      <>
                        <div>
                          <label className="text-xs text-gray-500">Société:</label>
                          <p className="font-medium">{selectedOrder.customer?.companyName || 'N/A'}</p>
                        </div>
                        {selectedOrder.customer?.siret && (
                          <div>
                            <label className="text-xs text-gray-500">SIRET:</label>
                            <p className="font-medium">{selectedOrder.customer.siret}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="font-medium mb-2">Adresse de livraison</h3>
                  <div className="text-sm space-y-2 bg-gray-50 p-4 rounded">
                    {(() => {
                      const addr = getAddress(selectedOrder.deliveryAddress);
                      return (
                        <>
                          <div>
                            <label className="text-xs text-gray-500">Rue:</label>
                            <p className="font-medium">{addr.street || 'N/A'}</p>
                          </div>
                          {selectedOrder.deliveryAddress?.addressComplement && (
                            <div>
                              <label className="text-xs text-gray-500">Complément:</label>
                              <p className="font-medium">{selectedOrder.deliveryAddress.addressComplement}</p>
                            </div>
                          )}
                          <div>
                            <label className="text-xs text-gray-500">Code postal:</label>
                            <p className="font-medium">{addr.postalCode || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Ville:</label>
                            <p className="font-medium">{addr.city || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Pays:</label>
                            <p className="font-medium">{addr.country}</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Delivery Method */}
              {selectedOrder.deliveryMethod && (
                <div>
                  <h3 className="font-medium mb-2">Mode de livraison</h3>
                  <div className="bg-gray-50 p-4 rounded">
                    <p className="font-medium">{selectedOrder.deliveryMethod.name}</p>
                    <p className="text-sm text-gray-600">{formatPrice(selectedOrder.deliveryMethod.price || 0)} €</p>
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div>
                <h3 className="font-medium mb-2">Articles commandés ({selectedOrder.items?.length || 0})</h3>
                <div className="space-y-4">
                  {selectedOrder.items?.map((item, index) => {
                    const config = item.configuration || {};
                    const optionsSupp = config.optionsSupp || config.options_supplementaires || {};
                    const fabricCalc = config.fabricCalculation || {};
                    
                    // Labels pour les fixations
                    const fixationLabels = {
                      'petit-ourlet': 'Petit ourlet',
                      'grand-ourlet': 'Grand ourlet',
                      'jonc': 'Jonc',
                      'jonc-magnetique': 'Jonc magnétique',
                      'fourreau': 'Fourreau',
                      'scratch': 'Scratch / Velcro',
                      'sangle': 'Sangle'
                    };
                    
                    // Labels pour lambrequin
                    const lambrequinLabels = {
                      'droit': 'Coupe droite',
                      'ondule': 'Vague 1',
                      'vagues': 'Vague 2',
                      'sans': 'Sans lambrequin'
                    };
                    
                    // Labels pour assemblage
                    const assemblageLabels = {
                      'haute-tenacite': 'Haute ténacité',
                      'tenara': 'Tenara (haute ténacité)',
                      'standard': 'Standard',
                      'renforce': 'Renforcé'
                    };
                    
                    return (
                      <div key={index} className="border rounded-lg overflow-hidden">
                        {/* En-tête article */}
                        <div className="bg-gray-50 p-3 flex justify-between items-center border-b">
                          <div>
                            <p className="font-bold text-lg">{item.productName || item.product_name || 'Produit'}</p>
                            <p className="text-sm text-gray-600">Quantité: {item.quantity}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">{formatPrice(item.unitPrice || item.unit_price || 0)} € / unité</p>
                            <p className="font-bold text-lg text-green-600">{formatPrice(item.totalPrice || item.total_price || 0)} €</p>
                          </div>
                        </div>
                        
                        {/* Détails de configuration */}
                        {config && (
                          <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                            {/* Colonne 1: Dimensions et fixations */}
                            <div className="space-y-3">
                              <div className="bg-blue-50 p-3 rounded">
                                <h4 className="font-semibold text-blue-800 mb-2">📐 Dimensions</h4>
                                <p><span className="text-gray-600">Largeur:</span> <strong>{config.largeur || config.width} cm</strong></p>
                                <p><span className="text-gray-600">Avancée:</span> <strong>{config.projection || config.avancee} cm</strong></p>
                              </div>
                              
                              <div className="bg-gray-50 p-3 rounded">
                                <h4 className="font-semibold text-gray-700 mb-2">🔧 Fixations</h4>
                                <p>
                                  <span className="text-gray-600">Fixation haute:</span> <strong>{fixationLabels[config.fixationHaute] || config.fixationHaute || 'N/A'}</strong>
                                  {config.diametreJoncHaute && <span className="text-blue-600 ml-1">(Ø {config.diametreJoncHaute} mm)</span>}
                                </p>
                                <p>
                                  <span className="text-gray-600">Fixation basse:</span> <strong>{fixationLabels[config.fixationBasse] || config.fixationBasse || 'N/A'}</strong>
                                  {config.diametreJoncBasse && <span className="text-blue-600 ml-1">(Ø {config.diametreJoncBasse} mm)</span>}
                                </p>
                              </div>
                              
                              {config.typeAssemblage && (
                                <div className="bg-gray-50 p-3 rounded">
                                  <h4 className="font-semibold text-gray-700 mb-2">🧵 Assemblage</h4>
                                  <p><strong>{assemblageLabels[config.typeAssemblage] || config.typeAssemblage}</strong></p>
                                </div>
                              )}
                            </div>
                            
                            {/* Colonne 2: Lambrequin et options */}
                            <div className="space-y-3">
                              <div className="bg-orange-50 p-3 rounded">
                                <h4 className="font-semibold text-orange-800 mb-2">🎪 Lambrequin</h4>
                                {config.lambrequin && config.lambrequin !== 'sans' ? (
                                  <>
                                    <p><span className="text-gray-600">Type:</span> <strong>{lambrequinLabels[config.lambrequin] || config.lambrequin}</strong></p>
                                    <p><span className="text-gray-600">Hauteur:</span> <strong>{config.hauteurLambrequin || config.hauteur_lambrequin} cm</strong></p>
                                    {(config.fixationLambrequin || config.fixation_lambrequin) && (
                                      <p>
                                        <span className="text-gray-600">Fixation:</span> <strong>{fixationLabels[config.fixationLambrequin || config.fixation_lambrequin] || config.fixationLambrequin || config.fixation_lambrequin}</strong>
                                        {(config.diametreJoncLambrequin || config.diametreLambrequin) && <span className="text-blue-600 ml-1">(Ø {config.diametreJoncLambrequin || config.diametreLambrequin} mm)</span>}
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-gray-500 italic">Sans lambrequin</p>
                                )}
                              </div>
                              
                              {/* Options supplémentaires */}
                              <div className="bg-purple-50 p-3 rounded">
                                <h4 className="font-semibold text-purple-800 mb-2">⚙️ Options</h4>
                                {(optionsSupp.renfortLyre || optionsSupp.textePersonnalise || optionsSupp.agrafes || optionsSupp.clips || optionsSupp.oeillets) ? (
                                  <ul className="space-y-1">
                                    {optionsSupp.renfortLyre && <li>✓ Renfort de lyre {config.renfortLyreDetails?.nombre > 1 ? `(×${config.renfortLyreDetails.nombre})` : ''}</li>}
                                    {optionsSupp.textePersonnalise && <li>✓ Jonc magnétique</li>}
                                    {optionsSupp.agrafes && <li>✓ Jonc à montage rapide</li>}
                                    {optionsSupp.clips && <li>✓ Type d'assemblage spécial</li>}
                                    {optionsSupp.oeillets && <li>✓ Lestage du lambrequin</li>}
                                  </ul>
                                ) : (
                                  <p className="text-gray-500 italic">Aucune option</p>
                                )}
                              </div>
                              
                              {/* Lestage */}
                              {config.lestageDetails && config.lestageDetails.nombreLests > 0 && (
                                <div className="bg-gray-50 p-3 rounded">
                                  <h4 className="font-semibold text-gray-700 mb-2">⚖️ Lestage</h4>
                                  <p><span className="text-gray-600">Diamètre tube:</span> <strong>{config.lestageDetails.diametreTube} mm</strong></p>
                                  <p><span className="text-gray-600">Nombre de lests:</span> <strong>{config.lestageDetails.nombreLests}</strong></p>
                                </div>
                              )}
                            </div>
                            
                            {/* Calculs de fabrication (pleine largeur) */}
                            {fabricCalc && fabricCalc.metrage_total && (
                              <div className="col-span-2 bg-green-50 p-3 rounded border border-green-200">
                                <h4 className="font-semibold text-green-800 mb-2">🏭 Données de fabrication</h4>
                                <div className="grid grid-cols-5 gap-2 text-center">
                                  <div>
                                    <p className="text-xs text-gray-500">Largeur totale</p>
                                    <p className="font-bold">{fabricCalc.largeur_totale} cm</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Avancée totale</p>
                                    <p className="font-bold">{fabricCalc.avancee_totale} cm</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Nb laizes</p>
                                    <p className="font-bold">{fabricCalc.nombre_laizes}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Métrage</p>
                                    <p className="font-bold">{fabricCalc.metrage_total} m</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Poids</p>
                                    <p className="font-bold">{(fabricCalc.poids_total / 1000).toFixed(2)} kg</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Order Summary */}
              <div className="border-t pt-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Sous-total:</span>
                    <span>{formatPrice(selectedOrder.subtotal || 0)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Frais de livraison:</span>
                    <span>{formatPrice(selectedOrder.deliveryCost || 0)} €</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total TTC:</span>
                    <span>{formatPrice(selectedOrder.totalTTC || 0)} €</span>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="text-xs text-gray-500 border-t pt-4">
                <p>Créée le: {formatDate(selectedOrder.createdAt)}</p>
                {selectedOrder.updated_at && (
                  <p>Mise à jour: {formatDate(selectedOrder.updated_at)}</p>
                )}
              </div>
            </div>

            <div className="p-6 border-t flex gap-3">
              <button
                onClick={() => {
                  handleDeleteClick(selectedOrder, { stopPropagation: () => {} });
                }}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded transition-colors"
              >
                Supprimer
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && orderToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer la commande <span className="font-medium">{orderToDelete.order_number}</span> ?
              Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded transition-colors"
              >
                Supprimer
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setOrderToDelete(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Envoyer par email</h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Envoyer les détails de la commande <span className="font-medium">{selectedOrder.order_number}</span> par email.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Adresse email</label>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="contact@toilestorebanne.com"
                className="w-full px-4 py-2 border rounded focus:outline-none focus:border-celadon"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded transition-colors disabled:opacity-50"
              >
                <Mail size={18} />
                {sendingEmail ? 'Envoi...' : 'Envoyer'}
              </button>
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
